import React from 'react';
import styled, { css } from 'styled-components';
import { typographyMixins } from '@ardoq/typography';
import { colors, space } from '@ardoq/design-tokens';
import { TextOverflow } from '@ardoq/popovers';
import {
  CloseIcon,
  ErrorIcon,
  IconSize,
  RefreshIcon,
  WarningIcon,
} from '@ardoq/icons';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import { FlexBox } from '@ardoq/layout';
import { type I18nLocale, t } from '@ardoq/i18n';

export enum TabStatus {
  DEFAULT,
  MODIFIED,
  SUCCESS,
  WARNING,
  ERROR,
}

type TabCloseButtonProps = {
  isActive: boolean;
  editable: boolean;
  tooltipText: string;
  onClear: () => void;
};

const TabCloseButton = ({
  isActive,
  editable,
  tooltipText,
  onClear,
}: TabCloseButtonProps) => {
  const handleClear = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onClear();
  };

  // Active tab with clear action
  if (isActive) {
    const Icon = editable ? CloseIcon : RefreshIcon;
    return (
      <Icon
        iconSize={IconSize.SMALL}
        data-tooltip-text={tooltipText}
        style={{ color: colors.grey500 }}
        deprecated_onClick={handleClear}
      />
    );
  }

  // Inactive editable tab with clear action (shows on hover)
  if (!isActive && editable) {
    return (
      <CloseIconOnHover
        iconSize={IconSize.SMALL}
        data-tooltip-text={tooltipText}
        style={{ color: colors.grey500 }}
        deprecated_onClick={handleClear}
      />
    );
  }

  return null;
};

type TabButtonProps = {
  i18nLocale: I18nLocale;
  isActive: boolean;
  label: string;
  tabIndex?: number;
  status?: TabStatus;
  onClick?: () => void;
  onClear?: () => void;
  isLast?: boolean;
  editable?: boolean;
  clearTabTooltipText?: string;
  removeTabTooltipText?: string;
  resetTabTooltipText?: string;
};

export const TabButton = ({
  i18nLocale,
  isActive,
  label,
  tabIndex,
  status = TabStatus.DEFAULT,
  onClick,
  onClear,
  isLast = false,
  editable = false,
  clearTabTooltipText,
  removeTabTooltipText = t(
    i18nLocale,
    'integrations:tabButton.removeTooltip',
    'Remove table configuration'
  ),
  resetTabTooltipText = t(
    i18nLocale,
    'integrations:tabButton.resetTooltip',
    'Reset table configuration'
  ),
}: TabButtonProps) => {
  const defaultTooltipText = editable
    ? removeTabTooltipText
    : resetTabTooltipText;
  const tooltipText = clearTabTooltipText || defaultTooltipText;
  return (
    <TabHeader
      gap="xsmall"
      align="center"
      $isActive={isActive}
      $isLast={isLast}
      $editable={editable}
      tabIndex={tabIndex}
      $status={status}
      onClick={onClick}
      role="tab"
      aria-label={label}
      aria-selected={isActive}
      data-testid="tabularMapping-configs-tab"
    >
      <TextOverflow>{label}</TextOverflow>
      <IconContainer>
        {!isActive && <StatusIcon isActive={isActive} status={status} />}
        {onClear && (
          <TabCloseButton
            isActive={isActive}
            editable={!!editable}
            tooltipText={tooltipText}
            onClear={onClear}
          />
        )}
      </IconContainer>
    </TabHeader>
  );
};

const StatusIcon = ({
  isActive,
  status,
}: {
  isActive: boolean;
  status: TabStatus;
}) => {
  switch (status) {
    case TabStatus.DEFAULT:
      return null;
    case TabStatus.MODIFIED:
      return null;
    case TabStatus.SUCCESS:
      return null;
    case TabStatus.WARNING:
      return (
        <WarningIcon
          iconSize={IconSize.SMALL}
          style={{ color: isActive ? colors.yellow600 : colors.iconWarning }}
        />
      );
    case TabStatus.ERROR:
      return (
        <ErrorIcon iconSize={IconSize.SMALL} style={{ color: colors.red600 }} />
      );
  }

  exhaustiveCheck(status);
};

const IconContainer = styled(FlexBox)`
  min-width: ${space.medium};
  gap: ${space.xxsmall};
`;

const TabHeader = styled(FlexBox)<{
  $isActive: boolean;
  $status?: TabStatus;
  $isLast?: boolean;
  $editable?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  tabIndex?: number;
}>`
  ${typographyMixins.text1}
  padding: ${space.medium} ${space.medium};
  color: ${p => getTextColor(p.$isActive, p.$status)};
  cursor: pointer;
  align-items: center;
  border-right: ${p =>
    p.$isLast ? 'none' : `1px solid ${colors.borderNeutral}`};
  max-width: 300px;
  ${p => (p.$isActive ? activeTab(p.$status) : inactiveTab)}
  ${p => (!p.$isActive ? hover : '')}
`;

const CloseIconOnHover = styled(CloseIcon)`
  opacity: 0;
  transition: opacity 0.2s ease;

  ${TabHeader}:hover & {
    opacity: 1;
  }
`;

const getTextColor = (isActive: boolean, status?: TabStatus): string => {
  if (status === TabStatus.DEFAULT && !isActive) {
    return colors.textDisabled;
  }
  return colors.textDefault;
};

const getBorderColor = (status?: TabStatus): string => {
  switch (status) {
    case TabStatus.ERROR:
      return colors.borderErrorStrong;
    case TabStatus.WARNING:
      return colors.borderWarningStrong;
    default:
      return colors.borderBrandStrong;
  }
};

const activeTab = (status?: TabStatus) => css`
  background: ${colors.white950};
  box-shadow: inset 0 3px 0 0 ${getBorderColor(status)};
`;

const inactiveTab = css`
  box-shadow: inset 0 3px 0 0 ${colors.borderNeutral};
`;

const hover = css`
  &:hover {
    background: ${colors.grey300};
  }
`;
